import { useEffect, useMemo, useState } from "react";
import {
  getMaterials,
  getEquipments,
  getPersonnels,
  deleteMaterial,
  deleteEquipment,
  deletePersonnel,
  updateMaterial,
  updateEquipment,
  updatePersonnel,
  createMaterial,
  createEquipment,
  createPersonnel
} from "../../api/const";
import { useParams } from "react-router-dom";
import PageHeader from "../../components/common/ui/PageHeader";

export default function UserDefine() {
  const { id } = useParams();
  const project = id; // FK(PK)로 그대로 보냄

  // 현재값
  const [materials, setMaterials] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [personnels, setPersonnels] = useState([]);

  // 원본(비교용)
  const [materials0, setMaterials0] = useState([]);
  const [equipments0, setEquipments0] = useState([]);
  const [personnels0, setPersonnels0] = useState([]);

  const [saving, setSaving] = useState(false);

  // 생성 로직
  // 자재
  const handleAddMaterial = async () => {
    try {
      const newMat = await createMaterial({
        name: "Untitled",
        spec: "Undefined",
        unit: "Undefined",
        project
      });
      setMaterials(prev => [...prev, newMat]);
      setMaterials0(prev => [...prev, newMat]); // 원본에도 추가
    } catch (err) {
      console.error("자재 추가 실패:", err);
      alert("자재 추가 실패");
    }
  };

  // 장비
  const handleAddEquipment = async () => {
    try {
      const newEq = await createEquipment({
        name: "Untitled",
        spec: "Undefined",
        project
      });
      setEquipments(prev => [...prev, newEq]);
      setEquipments0(prev => [...prev, newEq]);
    } catch (err) {
      console.error("장비 추가 실패:", err);
      alert("장비 추가 실패");
    }
  };

  // 인원
  const handleAddPersonnel = async () => {
    try {
      const newPer = await createPersonnel({
        work_type: "Undefined",
        category: "Undefined",
        project
      });
      setPersonnels(prev => [...prev, newPer]);
      setPersonnels0(prev => [...prev, newPer]);
    } catch (err) {
      console.error("인원 추가 실패:", err);
      alert("인원 추가 실패");
    }
  };


  const fetchAll = async () => {
    const [mats, eqs, pers] = await Promise.all([
      getMaterials(project),
      getEquipments(project),
      getPersonnels(project),
    ]);
    setMaterials(mats); setMaterials0(mats);
    setEquipments(eqs); setEquipments0(eqs);
    setPersonnels(pers); setPersonnels0(pers);
    console.log(mats)
  };

  useEffect(() => { fetchAll(); }, [project]);

  const handleDelete = async (type, rid) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    if (type === "material") await deleteMaterial(rid);
    if (type === "equipment") await deleteEquipment(rid);
    if (type === "personnel") await deletePersonnel(rid);
    fetchAll();
  };

  // ===== 인라인 변경 핸들러 =====
  const onMatChange = (id, field, value) =>
    setMaterials(prev => prev.map(m => (m.id === id ? { ...m, [field]: value } : m)));
  const onEqChange = (id, field, value) =>
    setEquipments(prev => prev.map(e => (e.id === id ? { ...e, [field]: value } : e)));
  const onPerChange = (id, field, value) =>
    setPersonnels(prev => prev.map(p => (p.id === id ? { ...p, [field]: value } : p)));

  // ===== 변경 여부 계산 =====
  const isChanged = (a, b, keys) => keys.some(k => (a?.[k] ?? null) !== (b?.[k] ?? null));
  const mapById = (arr) => Object.fromEntries(arr.map(x => [x.id, x]));

  const changed = useMemo(() => {
    const m0 = mapById(materials0), e0 = mapById(equipments0), p0 = mapById(personnels0);

    const mats = materials.filter(m => isChanged(m0[m.id], m, ["name", "spec", "unit"]));
    const eqs = equipments.filter(e => isChanged(e0[e.id], e, ["name", "spec"]));
    const pers = personnels.filter(p => isChanged(p0[p.id], p, ["work_type", "category"]));

    return { mats, eqs, pers };
  }, [materials, equipments, personnels, materials0, equipments0, personnels0]);

  const hasChanges = changed.mats.length + changed.eqs.length + changed.pers.length > 0;

  // ===== 저장: 변경된 것만 PUT =====
  const handleSave = async () => {
    if (!hasChanges) {
      alert("변경사항이 없습니다.");
      return;
    }
    setSaving(true);
    try {
      const matReqs = changed.mats.map(m =>
        updateMaterial(m.id, { name: m.name, spec: m.spec, unit: m.unit, project })
      );
      const eqReqs = changed.eqs.map(e =>
        updateEquipment(e.id, { name: e.name, spec: e.spec, project })
      );
      const perReqs = changed.pers.map(p =>
        updatePersonnel(p.id, { work_type: p.work_type, category: p.category, project })
      );

      await Promise.all([...matReqs, ...eqReqs, ...perReqs]);
      await fetchAll();
      alert("저장 완료!");
    } catch (err) {
      console.error(err);
      alert("저장 실패. 잠시 후 다시 시도해 주세요.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">

      <PageHeader>자재·장비·인원 현황</PageHeader>
      {/* 상단 헤더 */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`btn-save px-4 py-2 ${saving || !hasChanges ? "opacity-50 cursor-not-allowed" : ""
            }`}
        >
          {saving ? "저장 중..." : "저장"}
        </button>
      </div>

      {/* 3분할 레이아웃 */}
      <div className="grid grid-cols-3 gap-6">

        {/* ===================== 자재 ===================== */}
        <div className="bg-white border border rounded-lg shadow-sm p-4 flex flex-col">
          <h3 className="text-lg font-semibold mb-3 flex justify-between items-center">
            자재 목록
            <button onClick={handleAddMaterial} className="btn-basic-sm">
              + 추가
            </button>
          </h3>

          {/* 테이블 래퍼 */}
          <div className="overflow-x-auto border border-black">
            <table className="min-w-full table-fixed text-sm">
              <thead className="doc-thead">
                <tr>
                  <th className="doc-th w-32">이름</th>
                  <th className="doc-th w-32">규격</th>
                  <th className="doc-th w-20">단위</th>
                  <th className="doc-th w-20">누계</th>
                  <th className="px-2 py-1 font-medium w-12">삭제</th>
                </tr>
              </thead>

              <tbody>
                {materials.map((m) => (
                  <tr key={m.id} className="border-t border-black">
                    <td className="border-r border-black px-2 py-1">
                      <input
                        className="input-sm"
                        value={m.name ?? ""}
                        onChange={(e) => onMatChange(m.id, "name", e.target.value)}
                      />
                    </td>
                    <td className="border-r border-black px-2 py-1">
                      <input
                        className="input-sm w-full"
                        value={m.spec ?? ""}
                        onChange={(e) => onMatChange(m.id, "spec", e.target.value)}
                      />
                    </td>
                    <td className="border-r border-black px-2 py-1">
                      <input
                        className="input-sm w-full"
                        value={m.unit ?? ""}
                        onChange={(e) => onMatChange(m.id, "unit", e.target.value)}
                      />
                    </td>
                    <td className="border-r border-black px-2 py-1 text-right">
                      {m.real_total ?? 0}
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button
                        onClick={() => handleDelete("material", m.id)}
                        className="text-red-500 hover:underline"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


        {/* ===================== 장비 ===================== */}
        <div className="bg-white border rounded-lg shadow-sm p-4 flex flex-col">
          <h3 className="text-lg font-semibold mb-3 flex justify-between items-center">
            장비 목록
            <button onClick={handleAddEquipment} className="btn-basic-sm">
              + 추가
            </button>
          </h3>

          {/* 테이블 래퍼 */}
          <div className="overflow-x-auto border border-black">
            <table className="min-w-full table-fixed text-sm">
              <thead className="doc-thead">
                <tr>
                  <th className="doc-th w-32">이름</th>
                  <th className="doc-th w-32">규격</th>
                  <th className="doc-th w-20">누계</th>
                  <th className="px-2 py-1 font-medium w-12">삭제</th>
                </tr>
              </thead>

              <tbody>
                {equipments.map((e) => (
                  <tr key={e.id} className="border-t border-black">
                    <td className="border-r border-black px-2 py-1">
                      <input
                        className="input-sm"
                        value={e.name ?? ""}
                        onChange={(ev) => onEqChange(e.id, "name", ev.target.value)}
                      />
                    </td>

                    <td className="border-r border-black px-2 py-1">
                      <input
                        className="input-sm w-full"
                        value={e.spec ?? ""}
                        onChange={(ev) => onEqChange(e.id, "spec", ev.target.value)}
                      />
                    </td>

                    {/* real_total 추가 */}
                    <td className="border-r border-black px-2 py-1 text-right">
                      {e.real_total ?? 0}
                    </td>

                    <td className="px-2 py-1 text-center">
                      <button
                        onClick={() => handleDelete("equipment", e.id)}
                        className="text-red-500 hover:underline"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


        {/* ===================== 인원 ===================== */}
        <div className="bg-white border rounded-lg shadow-sm p-4 flex flex-col">
          <h3 className="text-lg font-semibold mb-3 flex justify-between items-center">
            인원 목록
            <button onClick={handleAddPersonnel} className="btn-basic-sm">
              + 추가
            </button>
          </h3>

          {/* 테이블 래퍼 */}
          <div className="overflow-x-auto border border-black">
            <table className="min-w-full table-fixed text-sm">
              <thead className="doc-thead">
                <tr>
                  <th className="doc-th w-32">공종</th>
                  <th className="doc-th w-32">구분</th>
                  <th className="doc-th w-20">누계</th>
                  <th className="px-2 py-1 font-medium w-12">삭제</th>
                </tr>
              </thead>

              <tbody>
                {personnels.map((p) => (
                  <tr key={p.id} className="border-t border-black">
                    <td className="border-r border-black px-2 py-1">
                      <input
                        className="input-sm"
                        value={p.work_type ?? ""}
                        onChange={(e) => onPerChange(p.id, "work_type", e.target.value)}
                      />
                    </td>

                    <td className="border-r border-black px-2 py-1">
                      <input
                        className="input-sm"
                        value={p.category ?? ""}
                        onChange={(e) => onPerChange(p.id, "category", e.target.value)}
                      />
                    </td>

                    {/* real_total 추가 */}
                    <td className="border-r border-black px-2 py-1 text-right">
                      {p.real_total ?? 0}
                    </td>

                    <td className="px-2 py-1 text-center">
                      <button
                        onClick={() => handleDelete("personnel", p.id)}
                        className="text-red-500 hover:underline"
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


      </div>
    </div>
  );

}
